<template>
    <q-card class="datos-items-card q-mt-lg">
        <q-card-section class="card-header">
            <div class="row items-center no-wrap">
                <q-icon name="inventory_2" size="32px" class="q-mr-md header-icon" />
                <div class="col">
                    <div class="text-h5 text-weight-bold">Items del Presupuesto</div>
                    <div class="text-caption text-grey-3">
                        {{ rows.length }} {{ rows.length === 1 ? 'producto' : 'productos' }}
                    </div>
                </div>
                <div class="col-auto">
                    <q-btn unelevated color="white" text-color="primary" icon="add_circle" label="Agregar"
                        @click="productoAdd" no-caps :disable="!checkPermission('presupuesto.item.agregar')" />
                </div>
            </div>
        </q-card-section>

        <q-separator />
        <q-card-section class="q-pa-none table-container">
            <q-table :columns="columns" row-key="id" :rows="rows" flat selection="multiple"
                v-model:selected="itemsSelected" class="modern-table" :rows-per-page-options="[10, 25, 50]">
                <template v-slot:body="props">
                    <q-tr :props="props" class="table-row">
                        <q-td>
                            <q-checkbox v-model="props.selected" />
                        </q-td>
                        <q-td key="id" :props="props">
                            <span class="id-badge">#{{ props.row.id }}</span>
                        </q-td>
                        <q-td key="imagen" :props="props">
                            <q-img :src="`${productosImagesPath}/${props.row.producto_id}.webp`" alt="product image"
                                fit="contain">
                                <template v-slot:error>
                                    <div class="row items-center justify-center full-height bg-grey-4">
                                        <q-icon name="broken_image" size="25px" color="grey-5" />
                                    </div>
                                </template>
                            </q-img>
                        </q-td>
                        <q-td key="producto" :props="props">
                            <div class="product-info">
                                <div class="product-code">{{ props.row.producto_id.trim() }}</div>
                                <div class="product-name">{{ props.row.producto?.B1_DESC.trim() }}</div>
                            </div>
                        </q-td>
                        <q-td key="empresa" :props="props">
                            <q-chip dense color="blue-1" text-color="blue-9" class="empresa-chip">
                                {{ props.row.empresa.desig }}
                            </q-chip>
                        </q-td>
                        <q-td key="plazo" :props="props">
                            <q-badge v-if="props.row.plazo" color="purple" :label="`${props.row.plazo} días`"
                                class="plazo-badge" />
                            <span v-else class="text-grey-5">N/A</span>
                        </q-td>
                        <q-td key="largo_pieza" :props="props" class="text-bold">
                            {{ props.row.largo_pieza ? numberFormat(props.row.largo_pieza, true) : '' }}
                            {{ (props.row.largo_pieza ? 'mts' : '') }}
                        </q-td>
                        <q-td key="cantidad_piezas" :props="props" class="text-bold">
                            {{ props.row.cantidad_piezas ? numberFormat(props.row.cantidad_piezas, true) : '' }}
                        </q-td>
                        <q-td key="cantidad" :props="props" class="text-bold">
                            {{ numberFormat(props.row.cantidad, true) }}
                            {{ props.row?.producto?.B1_UM }}
                        </q-td>
                        <q-td key="descuento" :props="props" class="text-bold discount-cell">
                            {{ props.row.descuento_comercial ? `${props.row.descuento_comercial}%` : '-' }}
                        </q-td>
                        <q-td key="precio_unitario" :props="props" class="text-bold price-cell">
                            {{ numberFormat(props.row.precio_unitario) }}
                        </q-td>
                        <q-td key="precio_con_descuento" :props="props" class="text-bold price-discount-cell">
                            {{ numberFormat(props.row.precio_unitario * (1 - (props.row.descuento_comercial || 0) /
                                100)) }}
                        </q-td>
                        <q-td key="total" :props="props" class="text-bold total-cell">
                            {{ numberFormat(props.row.total) }}
                        </q-td>
                        <q-td key="acciones" :props="props">
                            <div class="action-buttons">
                                <q-btn unelevated dense icon="edit" color="primary" size="sm"
                                    @click="productoOpen(props.row, 'edit')" class="action-btn"
                                    :disable="!checkPermissionByStatus('presupuesto.item.editar', presup.estado.id)">
                                    <q-tooltip>Editar</q-tooltip>
                                </q-btn>
                                <q-btn unelevated dense icon="visibility" color="secondary" size="sm"
                                    @click="productoOpen(props.row, 'view')" class="action-btn"
                                    :disable="!checkPermission('presupuesto.item.ver')">
                                    <q-tooltip>Ver</q-tooltip>
                                </q-btn>
                                <q-btn unelevated dense icon="delete" color="red" size="sm"
                                    @click="productoDelete(props.row.id)" :loading="itemDeleteLoading === props.row.id"
                                    class="action-btn"
                                    :disable="!checkPermissionByStatus('presupuesto.item.eliminar', presup.estado.id)">
                                    <q-tooltip>Eliminar</q-tooltip>
                                </q-btn>
                                <q-btn v-if="props.row.producto.B1_WGRUP === 'WCNE' && esEstadoDesglosable" unelevated dense size="sm"
                                    @click="presupuestoDesgloce(props.row)" color="positive" icon="mdi-arrow-decision-outline">
                                    <q-tooltip>Desgloce</q-tooltip>
                                </q-btn>
                            </div>
                        </q-td>
                    </q-tr>
                </template>
                <template v-slot:bottom-row>
                    <q-tr>
                        <q-td colspan="7" class="text-right text-bold text-h6">
                            Total Paneles: {{ numberFormat(totalPaneles, true) }} M2
                        </q-td>
                    </q-tr>
                </template>
            </q-table>

            <q-separator v-if="itemsSelected.length > 0" />

            <q-card-actions v-if="itemsSelected.length > 0" align="right" class="split-actions">
                <div class="selected-info">
                    <q-icon name="check_circle" color="primary" size="20px" class="q-mr-xs" />
                    <span class="text-body2 text-weight-medium">
                        {{ itemsSelected.length }} {{ itemsSelected.length === 1 ?
                            'item seleccionado' : 'items seleccionados' }}
                    </span>
                </div>
                <q-space />
                <q-btn unelevated color="purple" icon="mdi-basket-unfill" label="Desacopiar Items"
                    @click="presupDesacopiar" no-caps class="split-btn" v-if="presup?.estado.nombre === 'ACOPIO'" />
                <q-btn v-if="!esAcopio" unelevated color="orange" icon="call_split" label="Dividir Items" @click="presupSplit" no-caps
                    class="split-btn" />
            </q-card-actions>
        </q-card-section>
        <q-dialog v-model="productoDialog" persistent full-width>
            <q-card class="dialog-card">
                <q-toolbar class="dialog-header">
                    <q-icon name="shopping_cart" size="28px" class="q-mr-sm" />
                    <q-toolbar-title>Seleccione Producto</q-toolbar-title>
                    <q-space />
                    <q-btn unelevated dense round icon="close" color="white" text-color="primary" v-close-popup>
                        <q-tooltip>Cerrar</q-tooltip>
                    </q-btn>
                </q-toolbar>
                <q-card-section class="q-pa-none">
                    <ProductoGrid :is-search="true" :is-saved-presup="true" :presup-id="presup.id"
                        @item-added="onItemAdded" @item-updated="onItemUpdated" />
                </q-card-section>
                <q-card-actions align="right" class="q-pa-md">
                    <q-btn unelevated color="primary" icon="close" label="Cerrar" @click="productoDialog = false"
                        no-caps />
                </q-card-actions>
            </q-card>
        </q-dialog>
        <q-dialog v-model="dialog" persistent>
            <component :is="componentMapping[tipoProducto]" :oper="oper" :producto="producto" :formulario="formulario"
                :is-saved-presup="true" :presup-id="presup.id" @item-added="onItemAdded" @item-updated="onItemUpdated">
            </component>
        </q-dialog>
        <PresupuestoDesacopio :items="itemsSelected" v-model="dialogDesacopio" :presup-id="presup.id"
            @success="desacopioSuccess" />
        <DesgloceDialog v-model="desgloceDialogOpen" :camara="camara" :presupuesto-id="presup.id"
        @desgloce-confirmado="desgloceConfirmado"/>
        <q-inner-loading :showing="loading">
            <q-spinner-hourglass size="50px" color="primary" />
        </q-inner-loading>
    </q-card>
</template>

<script setup>
import { itemMapper } from "@/composables/itemMapper.js";
import { ref, computed } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import { useAuthStore } from "@/stores/useAuthStore.js";
import { useCartStore } from "@/stores/useCartStore.js";
import { useQuasar } from "quasar";
import componentMapping from "@/constants/componentMapping.js";
import numberFormat from "@/helpers/numberFormat";
import PresupuestoDesacopio from "./PresupuestoDesacopio.vue";
import PresupuestoService from "@/services/PresupuestoService";
import ProductoGrid from "@/pages/Productos/components/ProductoGrid.vue";
import DesgloceDialog from "./DesgloceDialog.vue";

const { checkPermission, checkPermissionByStatus } = useAuthStore();
const { presup } = defineProps({ presup: Object });
const $q = useQuasar();
const dialog = ref(false);
const formulario = ref({});
const itemDeleteLoading = ref(-1);
const itemsSelected = ref([]);
const loading = ref(false);
const oper = ref("edit");
const page = usePage();
const producto = ref({});
const productoDialog = ref(false);
const productosImagesPath = page.props.productosImagesPath;
const tipoProducto = ref("WPAN");
const dialogDesacopio = ref(false);
const desgloceDialogOpen = ref(false);
const camara = ref()

const esAcopio = computed(()=>presup?.estado?.nombre === 'ACOPIO')
const emit = defineEmits(["item-deleted", "item-added", "item-updated"]);
const esEstadoDesglosable = computed(() => {
    const estadosDesglosables = ["INGENIERIA APROBADA"];
    return estadosDesglosables.includes(presup?.estado?.nombre);
});
const columns = [
    { name: "id", label: "ID", field: "id", align: "left" },
    { name: "imagen", label: "Imagen", align: "center" },
    {
        name: "producto",
        label: "Producto",
        field: (row) =>
            row?.producto_id.trim() + " - " + row?.producto?.B1_DESC.trim(),
        align: "left",
    },
    {
        name: "largo_pieza",
        label: "Largo Pieza",
        field: "largo_pieza",
        align: "right",
        format: (val) => numberFormat(val, true),
    },
    {
        name: "cantidad_piezas",
        label: "Cantidad Piezas",
        field: "cantidad_piezas",
        align: "right",
        format: (val) => numberFormat(val, true),
    },
    {
        name: "cantidad",
        label: "Cantidad",
        field: "cantidad",
        align: "right",
        format: (val) => numberFormat(val, true),
    },
    {
        name: "descuento",
        label: "Descuento",
        field: "descuento_comercial",
        align: "right",
        format: (val) => (val ? `${val}%` : "-"),
    },
    {
        name: "precio_unitario",
        label: "Precio Unitario",
        field: "precio_unitario",
        align: "right",
        format: (val) => numberFormat(val),
    },
    {
        name: "precio_con_descuento",
        label: "Precio c/ Dto.",
        field: (row) => row.precio_unitario * (1 - (row.descuento_comercial || 0) / 100),
        align: "right",
        format: (val) => numberFormat(val),
    },
    {
        name: "total",
        label: "Total",
        field: "total",
        align: "right",
        format: (val) => numberFormat(val),
    },
    { name: "acciones", label: "Acciones", align: "right" },
];
const rows = ref(presup?.items || []);
const totalPaneles = ref(presup?.total_paneles || 0);

const productoOpen = (item, operType) => {
    tipoProducto.value = item.producto?.B1_WGRUP.trim();
    const { form, producto: prod } = itemMapper(item);
    if (!tipoProducto.value || !componentMapping[tipoProducto.value]) {
        $q.notify({
            message: "Falta asignar tipo de producto web",
            icon: "warning",
            color: "negative",
        });
        return;
    }
    oper.value = operType;
    producto.value = prod;
    formulario.value = form;
    dialog.value = true;
};

const productoAdd = () => {
    productoDialog.value = true;
};
const cartStore = useCartStore();
const productoDelete = (id) => {
    itemDeleteLoading.value = id;
    $q.dialog({
        title: "Eliminar Item",
        message: "¿Está seguro de eliminar este item?",
        cancel: true,
        persistent: true,
    }).onOk(async () => {
        await cartStore.itemDelete(id, true, presup.id);
        itemDeleteLoading.value = -1;
        emit("item-deleted");
    });
};

const presupDesacopiar = () => {
    if (itemsSelected.value.length === 0) {
        $q.notify({
            message: "Debe seleccionar al menos un item",
            icon: "warning",
            color: "negative",
        });
        return;
    }
    $q.dialog({
        title: "Desacopiar Items",
        message:
            "<b>¿Está seguro de desacopiar los items seleccionados?.</b> </br>Los items seleccionados serán movidos a un nuevo presupuesto en estado DESACOPIO.",
        html: true,
        cancel: true,
        persistent: true,
    }).onOk(() => {
        dialogDesacopio.value = true;
    });
};
const presupSplit = async () => {
    if (itemsSelected.value.length === 0) {
        $q.notify({
            message: "Debe seleccionar al menos un item",
            icon: "warning",
            color: "negative",
        });
        return;
    }
    $q.dialog({
        title: "Dividir Items",
        message:
            "<b>¿Está seguro de dividir los items seleccionados?.</b> </br>Los items seleccionados serán movidos a un nuevo presupuesto.",
        html: true,
        cancel: true,
        persistent: true,
    }).onOk(async () => {
        loading.value = true;
        try {
            const itemIds = itemsSelected.value.map((item) => item.id);
            const { nuevoPresupuesto } = await PresupuestoService.presupuestoSplit(
                presup.id,
                itemIds,
            );
            itemsSelected.value = [];
            router.visit(`/presupuestos/${nuevoPresupuesto.id}`);
        } catch (error) {
            return;
        } finally {
            loading.value = false;
        }
    });
};
const onItemAdded = () => emit("item-added");

const onItemUpdated = () => emit("item-updated");

const desacopioSuccess = () => {
    dialogDesacopio.value = false;
    itemsSelected.value = [];
    emit("item-updated");
};

const presupuestoDesgloce = (row) => {
    camara.value = row;
    desgloceDialogOpen.value = true;
};

const desgloceConfirmado = () => {
    desgloceDialogOpen.value = false;
    emit("item-updated");
};
</script>

<style lang="scss" scoped>
@use 'sass:color';

.datos-items-card {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;

    &:hover {
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    }
}

.card-header {
    background: linear-gradient(135deg, #5e35b1 0%, #4527a0 100%);
    color: white;
    padding: 24px;

    .header-icon {
        opacity: 0.95;
    }

    .add-btn {
        font-weight: 500;
        padding: 8px 20px;
        border-radius: 8px;
        transition: all 0.2s ease;

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(255, 255, 255, 0.3);
        }
    }
}

.table-container {
    background: #fafafa;
}

.modern-table {
    :deep(.q-table__top) {
        padding: 16px;
        background: white;
    }

    :deep(thead tr th) {
        background: linear-gradient(to bottom, #f5f5f5 0%, #eeeeee 100%);
        color: #424242;
        font-weight: 600;
        font-size: 13px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        padding: 16px 12px;
        border-bottom: 2px solid #e0e0e0;
    }

    :deep(tbody) {
        background: white;
    }

    .table-row {
        transition: all 0.2s ease;

        &:hover {
            background: linear-gradient(to right, #f3e5f5 0%, #f8f9fa 100%);
            transform: translateX(4px);
        }

        td {
            padding: 12px;
            border-bottom: 1px solid #f0f0f0;
        }
    }

    .id-badge {
        display: inline-block;
        background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
        color: #1565c0;
        padding: 4px 10px;
        border-radius: 12px;
        font-size: 12px;
        font-weight: 600;
    }

    .product-avatar {
        transition: all 0.2s ease;

        &:hover {
            transform: scale(1.1);
        }
    }

    .product-info {
        .product-code {
            font-size: 12px;
            font-weight: 600;
            color: #5e35b1;
            margin-bottom: 4px;
        }

        .product-name {
            font-size: 13px;
            color: #424242;
            line-height: 1.3;
        }
    }

    .empresa-chip {
        font-weight: 500;
        font-size: 12px;
    }

    .plazo-badge {
        font-size: 11px;
        padding: 4px 8px;
    }

    .discount-cell {
        color: #e65100;
        font-size: 14px;
    }

    .price-cell {
        color: #1976d2;
        font-size: 14px;
    }

    .price-discount-cell {
        color: #6a1b9a;
        font-size: 14px;
    }

    .total-cell {
        color: #388e3c;
        font-size: 14px;
        background: linear-gradient(to right, transparent 0%, #e8f5e9 100%);
    }

    .action-buttons {
        display: flex;
        gap: 8px;

        .action-btn {
            border-radius: 6px;
            transition: all 0.2s ease;

            &:hover {
                transform: translateY(-2px);
                box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
            }
        }
    }
}

.split-actions {
    padding: 16px 24px;
    background: linear-gradient(to right, #fff3e0 0%, #ffffff 100%);
    border-top: 2px solid #ffb74d;

    .selected-info {
        display: flex;
        align-items: center;
        color: #424242;
        font-weight: 500;
    }

    .split-btn {
        font-weight: 500;
        padding: 8px 24px;
        border-radius: 8px;
        transition: all 0.2s ease;

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
        }
    }
}

.dialog-card {
    max-width: 1400px;
    border-radius: 16px;
    overflow: hidden;
}

.dialog-header {
    background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
    color: white;
    padding: 16px 24px;
}

// Responsive
@media (max-width: 1024px) {
    .card-header {
        padding: 16px;

        .text-h5 {
            font-size: 18px;
        }

        .add-btn {
            padding: 6px 16px;
        }
    }

    .modern-table {
        :deep(thead tr th) {
            font-size: 11px;
            padding: 12px 8px;
        }

        .table-row td {
            padding: 10px 8px;
        }

        .product-avatar {
            width: 50px;
            height: 50px;
        }
    }

    .split-actions {
        flex-direction: column;
        gap: 12px;

        .split-btn {
            width: 100%;
        }
    }
}

@media (max-width: 600px) {
    .modern-table {
        font-size: 12px;

        .product-info {

            .product-code,
            .product-name {
                font-size: 11px;
            }
        }

        .action-buttons {
            flex-direction: column;
            gap: 4px;
        }
    }
}

// Animation
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.datos-items-card {
    animation: fadeInUp 0.4s ease;
}
</style>
